import endpoints from '@/api/endpoints';
import { http, HttpResponse } from 'msw';
import { Checkbook } from '@/interfaces/checkbook';

const API_BASE_URL = endpoints.baseURL;

// ── Mock data ─────────────────────────────────────────────────────────────
// Historique des demandes de chéquier (mélange volontaire de statuts et de dates pour couvrir le
// tri décroissant par défaut). NB: ce fichier est aussi ajouté indépendamment sur IB1-300 — les
// deux branches convergeront sans conflit une fois mergées dans feat/IB1-291 (contenu identique).

const baseUser = { id: 'user-001', fullName: 'FARHA IMPORT AND EXPORT CO LTD', username: 'farhaimportexport66' };

const baseAccount = {
  id: 'acc-001',
  accountId: '0b679eab-db5e-4cb4-b07e-2a8185bb2757',
  name: 'FARHA IMPORT AND EXPORT CO LTD',
  number: '00010804501-75',
  branchCode: '0001',
  balanceInReferenceCurrency: 82.33,
  currency: { id: '2d0a7522-7e01-44ae-b387-3033bafd5cf5', alphabeticCode: 'USD' },
  status: 'ACTIVE',
};

export const mockCheckbooks: Checkbook[] = [
  {
    id: 'chk-002',
    requestId: 'req-002',
    createdAt: '2026-07-25T09:00:00Z',
    requestStatus: 'REQUESTED',
    type: { id: 'chk-type-01', code: 'STD25', name: 'Standard', currency: 'USD', label: '25 feuilles', numberOfLeaves: 25, fee: 5 },
    account: baseAccount,
    requiredValidatorsCount: 0,
    validators: [],
    user: baseUser,
  },
  {
    id: 'chk-001',
    requestId: 'req-001',
    createdAt: '2026-07-20T09:00:00Z',
    requestStatus: 'COLLECTED',
    type: { id: 'chk-type-02', code: 'STD50', name: 'Standard', currency: 'USD', label: '50 feuilles', numberOfLeaves: 50, fee: 8 },
    account: baseAccount,
    requiredValidatorsCount: 2,
    validators: [{ userId: 'user-002', firstName: 'Jean', lastName: 'Dupont', approvedAt: '2026-07-21T10:00:00Z' }],
    user: baseUser,
  },
  {
    id: 'chk-003',
    requestId: 'req-003',
    createdAt: '2026-07-10T09:00:00Z',
    requestStatus: 'CANCELED',
    type: { id: 'chk-type-01', code: 'STD25', name: 'Standard', currency: 'USD', label: '25 feuilles', numberOfLeaves: 25, fee: 5 },
    account: baseAccount,
    requiredValidatorsCount: 0,
    validators: [],
    user: baseUser,
  },
  {
    id: 'chk-004',
    requestId: 'req-004',
    createdAt: '2026-06-01T09:00:00Z',
    requestStatus: 'DENIED',
    type: { id: 'chk-type-01', code: 'STD25', name: 'Standard', currency: 'USD', label: '25 feuilles', numberOfLeaves: 25, fee: 5 },
    account: baseAccount,
    requiredValidatorsCount: 0,
    validators: [],
    user: baseUser,
  },
  // IB1-303 (épique #5428, F1.2/F5.1) : lignes de test volontairement laissées dans les mocks pour
  // déclencher au clic les 3 cas d'erreur du panneau détail (404/403/générique) sans backend réel.
  // Libellé "TEST - Erreur ..." pour ne pas les confondre avec de vraies demandes. Ajoutées en fin
  // de tableau (pas en tête) pour ne pas perturber mockCheckbooks[0], utilisé structurellement par
  // le test CheckbookLegacyFormats.integration.test.tsx (IB1-301). Statut PROCESSING (IB1-415,
  // aligné sur le vrai enum backend CheckbookRequestStatus) plutôt que REQUESTED, pour ne pas casser
  // l'hypothèse d'unicité de CheckbookHistory.integration.test.tsx (IB1-300), qui vérifie la
  // présence de chaque statut réel via getByText.
  {
    id: 'chk-unknown',
    requestId: 'req-test-404',
    createdAt: '2026-08-04T09:00:00Z',
    requestStatus: 'PROCESSING',
    type: { id: 'chk-type-test', code: 'TEST', name: 'Test', currency: 'USD', label: 'TEST - Erreur 404', numberOfLeaves: 25, fee: 5 },
    account: baseAccount,
    requiredValidatorsCount: 0,
    validators: [],
    user: { ...baseUser, fullName: 'TEST - Erreur 404' },
  },
  {
    id: 'chk-forbidden',
    requestId: 'req-test-403',
    createdAt: '2026-08-04T09:01:00Z',
    requestStatus: 'PROCESSING',
    type: { id: 'chk-type-test', code: 'TEST', name: 'Test', currency: 'USD', label: 'TEST - Erreur 403', numberOfLeaves: 25, fee: 5 },
    account: baseAccount,
    requiredValidatorsCount: 0,
    validators: [],
    user: { ...baseUser, fullName: 'TEST - Erreur 403' },
  },
  {
    id: 'chk-error-500',
    requestId: 'req-test-500',
    createdAt: '2026-08-04T09:02:00Z',
    requestStatus: 'PROCESSING',
    type: { id: 'chk-type-test', code: 'TEST', name: 'Test', currency: 'USD', label: 'TEST - Erreur 500', numberOfLeaves: 25, fee: 5 },
    account: baseAccount,
    requiredValidatorsCount: 0,
    validators: [],
    user: { ...baseUser, fullName: 'TEST - Erreur 500' },
  },
];

// ── Handlers ──────────────────────────────────────────────────────────────

/**
 * Historique des demandes de chéquier — cible endpoints.fetchCheckbooks quelle que soit sa valeur
 * (endpoint provisoire tant que IB1-300 n'est pas mergé ici).
 * Supporte le tri par date de création (createdAt,desc / createdAt,asc) et la pagination.
 */
export const getCheckbooksHandler = http.get<object, any, any>(`${API_BASE_URL}${endpoints.fetchCheckbooks}`, async ({ request }) => {
  const url = new URL(request.url);
  const page = Number(url.searchParams.get('page')) || 0;
  const size = Number(url.searchParams.get('size')) || 5;
  const sort = url.searchParams.get('sort') || 'createdAt,desc';
  const direction = sort.includes('asc') ? 'asc' : 'desc';

  const sorted = [...mockCheckbooks].sort((a, b) => {
    const diff = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    return direction === 'desc' ? -diff : diff;
  });

  const start = page * size;
  const end = start + size;
  const paginated = sorted.slice(start, end);

  return HttpResponse.json({
    data: paginated,
    totalElements: sorted.length,
    totalPages: Math.ceil(sorted.length / size),
  });
});

export const checkbookHandlers = [getCheckbooksHandler];
